/**
 * AppointmentRefreshTimer Component
 *
 * Shows a 2-minute countdown for auto-refreshing dashboard appointments.
 * Includes pause/play controls and triggers the supplied refresh callback
 * whenever the timer reaches zero.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play, Timer } from 'lucide-react';

const APPOINTMENT_REFRESH_SECONDS = 120;

interface AppointmentRefreshTimerProps {
  onRefresh: () => void;
}

export function AppointmentRefreshTimer({ onRefresh }: AppointmentRefreshTimerProps) {
  const [timeLeft, setTimeLeft] = useState(APPOINTMENT_REFRESH_SECONDS);
  const [isPaused, setIsPaused] = useState(false);
  const nextRefreshAtRef = useRef<number | null>(null);
  const timeLeftRef = useRef(APPOINTMENT_REFRESH_SECONDS);

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  const startTimer = useCallback(() => {
    nextRefreshAtRef.current = Date.now() + timeLeftRef.current * 1000;

    const tick = () => {
      const nextRefreshAt = nextRefreshAtRef.current;
      if (!nextRefreshAt) return;

      const remaining = Math.max(0, Math.ceil((nextRefreshAt - Date.now()) / 1000));
      setTimeLeft(remaining);

      if (remaining === 0) {
        onRefresh();
        nextRefreshAtRef.current = Date.now() + APPOINTMENT_REFRESH_SECONDS * 1000;
        setTimeLeft(APPOINTMENT_REFRESH_SECONDS);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [onRefresh]);

  useEffect(() => {
    if (isPaused) {
      return;
    }

    const stop = startTimer();
    return stop;
  }, [isPaused, startTimer]);

  const handleTogglePause = () => {
    if (isPaused) {
      setIsPaused(false);
      return;
    }

    setIsPaused(true);
  };

  return (
    <>
      <div
        className="flex items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-3 py-1.5 text-pranik-brand"
        title="Auto-refreshing latest appointments"
      >
        <Timer className="h-4 w-4" />
        <span className="font-mono text-sm font-semibold leading-tight">
          {`${Math.floor(timeLeft / 60)
            .toString()
            .padStart(2, '0')}:${(timeLeft % 60).toString().padStart(2, '0')}`}
        </span>
      </div>
      <button
        type="button"
        onClick={handleTogglePause}
        className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-900"
        title={isPaused ? 'Resume auto-refresh' : 'Pause auto-refresh'}
      >
        {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
      </button>
    </>
  );
}
